import React from "react";
import { FaFire, FaRegCheckCircle, FaClock } from "react-icons/fa";
import { useAsanaData } from "contexts/AsanaContext";
import { useDealData } from "contexts/DealContext";

const AsanaStatusBar = () => {
    const { currentDealName } = useDealData();
    const {
        matchDealToSection,
        isSectionHot,
        loading,
        StatusBadge
    } = useAsanaData();

    // Get the matching Asana section and tasks if any
    const match = matchDealToSection(currentDealName);
    const matchedSection = match?.section;
    const sectionTasks = match?.tasks || [];
    const isHotSection = matchedSection ? isSectionHot(matchedSection) : false;

    if (loading) {
        return (
            <div className="w-full bg-white rounded-md shadow-sm p-2 mb-4 overflow-hidden">
                <div className="animate-pulse flex space-x-4">
                    <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </div>
                </div>
            </div>
        );
    }

    // if (error || !currentDealName || !matchedSection) {
    //     return null; // Don't display anything if there's an error or no match
    // }

    // Sort tasks by status for a better flow
    const sortedTasks = [...sectionTasks].sort((a, b) => {
        const statusA = a.customFields?.Status?.toLowerCase() || "";
        const statusB = b.customFields?.Status?.toLowerCase() || "";

        // Define order of statuses for sorting
        const statusOrder = {
            "complete": 5,
            "in contract": 4,
            "under loi": 3,
            "sent": 2,
            "under review": 1,
            "declined": 0
        };

        let orderA = -1;
        let orderB = -1;

        // Find the matching status for task A
        for (const [key, value] of Object.entries(statusOrder)) {
            if (statusA.includes(key)) {
                orderA = value;
                break;
            }
        }

        // Find the matching status for task B
        for (const [key, value] of Object.entries(statusOrder)) {
            if (statusB.includes(key)) {
                orderB = value;
                break;
            }
        }

        return orderB - orderA; // Sort from highest to lowest status
    });

    if (sortedTasks.length === 0) {
        return null; // Don't display if no tasks
    }

    // Status indicators for the progress bar
    const getStatusIndicator = (task) => {
        const status = task.customFields?.Status?.toLowerCase() || "";

        if (status.includes("complete")) {
            return <FaRegCheckCircle className="text-green-500" size={16} />;
        } else if (status.includes("in contract") || status.includes("under loi")) {
            return <FaRegCheckCircle className="text-blue-500" size={16} />;
        } else if (status.includes("sent")) {
            return <FaClock className="text-cyan-500" size={16} />;
        } else if (status.includes("under review")) {
            return <FaClock className="text-orange-500" size={16} />;
        } else if (status.includes("declined")) {
            return <FaRegCheckCircle className="text-red-500" size={16} />;
        } else {
            return <FaClock className="text-gray-500" size={16} />;
        }
    };

    return (
        <div className="w-full bg-white dark:bg-navy-800 rounded-md shadow-sm p-2 mb-4">
            <div className="flex items-center mb-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-white flex items-center">
                    Deal Status
                    {isHotSection && (
                        <span className="ml-2 text-red-500" title="Hot Deal">
                            <FaFire />
                        </span>
                    )}
                </h4>
                <span className="ml-auto text-xs text-gray-500">
                    {sectionTasks.length} task{sectionTasks.length !== 1 ? 's' : ''}
                </span>
            </div>

            <div className="flex items-center justify-between w-full overflow-x-auto pb-1">
                <div className="relative flex items-center min-w-full">
                    {/* Progress bar background */}
                    <div className="absolute h-1 bg-gray-200 dark:bg-navy-700 w-full top-1/2 transform -translate-y-1/2 z-0"></div>

                    {/* Tasks as status points on the bar */}
                    <div className="flex justify-evenly w-full relative z-10">
                        {sortedTasks.map((task, index) => (
                            <div
                                key={task.id}
                                className="flex flex-col items-center mx-2"
                                style={{
                                    flex: '1 0 auto',
                                    maxWidth: '120px'
                                }}
                            >
                                <div className="mb-1 text-xs text-center truncate w-full" title={task.name}>
                                    {task.name}
                                </div>

                                <div className="flex items-center justify-center">
                                    <a
                                        href={task.permalinkUrl || `https://app.asana.com/0/${task.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-white dark:bg-navy-800 rounded-full p-1.5 shadow-md hover:shadow-lg transition-shadow"
                                        title={`View ${task.name} in Asana`}
                                    >
                                        {getStatusIndicator(task)}
                                    </a>
                                </div>

                                <div className="mt-1">
                                    <StatusBadge task={task} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AsanaStatusBar; 